import { activeTopics } from "./selectors";
import type { Topic } from "../types";

const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"] as const;

export type SourceTag = "热点补充" | "内容助理";

export function sourceTag(batch: string): SourceTag {
  return batch.includes("热点") ? "热点补充" : "内容助理";
}

export function weekBounds(today: string): { start: string; end: string } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!match) return { start: today, end: today };
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const offset = (date.getUTCDay() + 6) % 7;
  const start = new Date(date);
  start.setUTCDate(date.getUTCDate() - offset);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const iso = (value: Date) => value.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}

export function datesInWeek(start: string): string[] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(start);
  if (!match) return [];
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Array.from({ length: 7 }, (_, index) => {
    const next = new Date(date);
    next.setUTCDate(date.getUTCDate() + index);
    return next.toISOString().slice(0, 10);
  });
}

export function publishDay(topic: Topic): string | null {
  const actual = topic.actualPublishAt?.slice(0, 10);
  if (actual && /^\d{4}-\d{2}-\d{2}$/.test(actual)) return actual;
  return topic.plannedPublishDate;
}

export function publishedThisWeek(topics: Topic[], today: string): number {
  const { start, end } = weekBounds(today);
  return activeTopics(topics).filter((topic) => {
    const date = publishDay(topic);
    return topic.status === "已完成" && !!date && date >= start && date <= end;
  }).length;
}

/** This week minus last week. Null when both weeks have no completed posts. */
export function publishedWeekDelta(topics: Topic[], today: string): number | null {
  const current = publishedThisWeek(topics, today);
  const { start } = weekBounds(today);
  const previous = publishedThisWeek(topics, shiftIso(start, -1));
  if (current === 0 && previous === 0) return null;
  return current - previous;
}

function shiftIso(iso: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days));
  return date.toISOString().slice(0, 10);
}

export function shanghaiTimeLabel(value: string | null): string {
  if (!value || !value.includes("T")) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Shanghai",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

export function referenceHref(topic: Topic): string | null {
  const direct = topic.referenceUrl?.trim();
  if (direct) return direct;
  const source = (topic.sources ?? "").trim();
  return /^https?:\/\//i.test(source) ? source : null;
}

export function greetingForHour(hour: number): string {
  if (hour < 11) return "早上好";
  if (hour < 14) return "中午好";
  if (hour < 18) return "下午好";
  return "晚上好";
}

export function dateLabel(today: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!match) return today;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const month = Number(match[2]);
  const day = Number(match[3]);
  return `${month}月${day}日 周${WEEKDAYS[date.getUTCDay()]}`;
}

export function shanghaiHour(now = new Date()): number {
  const hour = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Shanghai",
    hour: "2-digit",
    hourCycle: "h23",
  }).format(now);
  return Number(hour);
}
